import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TiptapImage from "@tiptap/extension-image";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link2,
  List,
  ListOrdered,
  Image as ImageIcon,
  ChevronDown,
  Sparkles,
  CheckCircle2,
  Loader2,
  XCircle,
  Type,
  Highlighter,
  X,
  Save,
  Code2,
} from "lucide-react";
import { API_URL, STORAGE } from "@/_config";
import { useAuth } from "@/context/AuthContext";
import VariablesModal from "./VariablesModal";
import AiEmailModal from "./AiEmailModal";

// ---------------------------------------------------------------------------
// Custom FontSize extension (adds fontSize attribute to TextStyle mark)
// ---------------------------------------------------------------------------
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el: HTMLElement) => el.style.fontSize || null,
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setFontSize: (size: string) => ({ chain }: any) =>
        chain().setMark("textStyle", { fontSize: size }).run(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      unsetFontSize: () => ({ chain }: any) =>
        chain().setMark("textStyle", { fontSize: null }).run(),
    };
  },
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const FONT_SIZES = ["12px", "13px", "14px", "16px", "18px", "20px", "24px", "28px", "32px"];

const TEXT_COLORS = [
  "#000000", "#374151", "#6b7280", "#9ca3af",
  "#dc2626", "#ea580c", "#d97706", "#16a34a",
  "#0284c7", "#7c3aed", "#db2777", "#0f766e",
];

const HIGHLIGHT_COLORS = [
  "#fef08a", "#bbf7d0", "#bfdbfe", "#fecdd3",
  "#fed7aa", "#e9d5ff", "#99f6e4", "#fde68a",
  "transparent",
];

// ---------------------------------------------------------------------------
// ColorPicker popover
// ---------------------------------------------------------------------------
interface ColorPickerProps {
  colors: string[];
  onSelect: (color: string) => void;
  onClose: () => void;
  customLabel?: string;
}

function ColorPicker({ colors, onSelect, onClose, customLabel = "Custom" }: ColorPickerProps) {
  const customRef = useRef<HTMLInputElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div className="color-picker-popover" ref={ref}>
      <div className="color-picker-grid">
        {colors.map((c) => (
          <button
            key={c}
            type="button"
            className="color-swatch"
            style={{
              background: c === "transparent" ? "linear-gradient(135deg, #fff 45%, #f87171 45%, #f87171 55%, #fff 55%)" : c,
              border: c === "transparent" ? "1px solid #e5e7eb" : "1px solid rgba(0,0,0,0.12)",
            }}
            title={c === "transparent" ? "Remove highlight" : c}
            onClick={() => { onSelect(c); onClose(); }}
          />
        ))}
      </div>
      <div className="color-picker-custom">
        <span className="color-picker-custom-label">{customLabel}</span>
        <input
          ref={customRef}
          type="color"
          className="color-picker-native"
          onChange={(e) => onSelect(e.target.value)}
        />
        <button
          type="button"
          className="color-picker-custom-btn"
          onClick={() => customRef.current?.click()}
        >
          Pick color
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImageInsertDialog
// ---------------------------------------------------------------------------
interface ImageInsertDialogProps {
  onInsert: (src: string, alt: string) => void;
  onClose: () => void;
}

function ImageInsertDialog({ onInsert, onClose }: ImageInsertDialogProps) {
  const [mode, setMode] = useState<"url" | "upload">("url");
  const [src, setSrc] = useState("");
  const [alt, setAlt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError("");
    setSrc("");
    try {
      const raw = localStorage.getItem(STORAGE);
      const stored = raw ? JSON.parse(raw) : null;
      const token = stored?.token ?? stored?.access_token ?? "";
      const form = new FormData();
      form.append("image", file);
      const res = await fetch(API_URL.EMAIL_IMAGES, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setUploadError(err?.message ?? "Upload failed. Please try again.");
        return;
      }
      const data = await res.json();
      setSrc(data?.data?.url ?? "");
    } catch {
      setUploadError("Upload failed. Please check your connection.");
    } finally {
      setUploading(false);
    }
  };

  const handleInsert = () => {
    if (!src.trim()) return;
    onInsert(src.trim(), alt.trim());
    onClose();
  };

  return (
    <div className="img-dialog-overlay">
      <div className="img-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="img-dialog-header">
          <span className="img-dialog-title">Insert Image</span>
          <button type="button" className="img-dialog-close" onClick={onClose}>
            <X size={15} />
          </button>
        </div>

        <div className="img-dialog-tabs">
          <button
            type="button"
            className={`img-dialog-tab${mode === "url" ? " active" : ""}`}
            onClick={() => { setMode("url"); setSrc(""); }}
          >
            URL
          </button>
          <button
            type="button"
            className={`img-dialog-tab${mode === "upload" ? " active" : ""}`}
            onClick={() => { setMode("upload"); setSrc(""); }}
          >
            Upload
          </button>
        </div>

        <div className="img-dialog-body">
          {mode === "url" ? (
            <input
              type="url"
              className="img-dialog-input"
              placeholder="https://example.com/image.jpg"
              value={src}
              onChange={(e) => setSrc(e.target.value)}
              autoFocus
            />
          ) : (
            <>
              <div
                className={`img-upload-area${src ? " img-upload-area--has-file" : ""}`}
                onClick={() => !uploading && fileRef.current?.click()}
              >
                {uploading ? (
                  <div className="img-upload-placeholder">
                    <Loader2 size={22} className="img-upload-spinner" />
                    <span>Uploading…</span>
                  </div>
                ) : src ? (
                  <img src={src} alt="preview" className="img-upload-preview" />
                ) : (
                  <div className="img-upload-placeholder">
                    <ImageIcon size={26} />
                    <span>Click to choose an image</span>
                    <span className="img-upload-hint">PNG, JPG, GIF, WebP — max 5 MB</span>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
              {uploadError && (
                <p style={{ margin: 0, fontSize: 12, color: "#b91c1c" }}>{uploadError}</p>
              )}
            </>
          )}

          <input
            type="text"
            className="img-dialog-input"
            placeholder="Alt text (optional)"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
          />
        </div>

        <div className="img-dialog-footer">
          <button type="button" className="img-dialog-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="img-dialog-insert"
            onClick={handleInsert}
            disabled={!src.trim() || uploading}
          >
            Insert Image
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar divider
// ---------------------------------------------------------------------------
function Divider() {
  return <span className="email-toolbar-divider" />;
}

// ---------------------------------------------------------------------------
// Main EmailEditor
// ---------------------------------------------------------------------------
interface EmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  saveStatus?: "idle" | "saving" | "saved" | "error";
  onSave?: () => void;
}

export default function EmailEditor({
  value,
  onChange,
  disabled = false,
  hasError = false,
  saveStatus = "idle",
  onSave,
}: EmailEditorProps) {
  const { user } = useAuth();
  const isTrialExpired =
    user?.planStatus === 'trialing' &&
    !!user?.organization?.trial_ends_at &&
    new Date() > new Date(user.organization.trial_ends_at);
  const isPlanDeactivated =
    user?.planStatus === 'cancelled' || user?.planStatus === 'unpaid';
  const hasGoldPlusAccess =
    !!(user?.organization?.plan_features?.ai_features) &&
    !isTrialExpired &&
    !isPlanDeactivated;

  const [showVariables, setShowVariables] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [variablesAnchor, setVariablesAnchor] = useState<DOMRect | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [isSourceMode, setIsSourceMode] = useState(false);
  const [sourceHtml, setSourceHtml] = useState(value);
  const isSourceModeRef = useRef(false);

  const variablesBtnRef = useRef<HTMLButtonElement>(null);
  const colorBtnRef = useRef<HTMLButtonElement>(null);
  const highlightBtnRef = useRef<HTMLButtonElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      TiptapImage.configure({ inline: false, allowBase64: true }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Placeholder.configure({ placeholder: "Write your email here..." }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sync content when value changes externally (e.g. switching variants)
  useEffect(() => {
    if (isSourceModeRef.current) {
      setSourceHtml(value);
      return;
    }
    if (!editor) return;
    if (editor.isFocused) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleSourceToggle = () => {
    if (isSourceMode) {
      // Switching back to rich text — parse the raw HTML into TipTap
      editor?.commands.setContent(sourceHtml, { emitUpdate: false });
      setIsSourceMode(false);
      isSourceModeRef.current = false;
    } else {
      // Switching to source mode — expose the current HTML for editing
      const html = editor?.getHTML() ?? value;
      setSourceHtml(html);
      setIsSourceMode(true);
      isSourceModeRef.current = true;
    }
  };

  // Sync editable state
  useEffect(() => {
    if (!editor) return;
    editor.setOptions({ editable: !disabled });
  }, [editor, disabled]);

  const handleLinkToggle = () => {
    if (!editor) return;
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = window.prompt("Enter URL", "https://");
      if (url) {
        const trimmed = url.trim();
        const applied = editor.chain().focus().setLink({ href: trimmed }).run();
        if (!applied) {
          window.alert("Invalid URL. Only http, https, mailto, and similar protocols are allowed.");
        }
      }
    }
  };

  const handleImageInsert = (src: string, alt: string) => {
    editor?.chain().focus().setImage({ src, alt }).run();
  };

  // Determine current font size from editor selection
  const currentFontSize = editor?.getAttributes("textStyle")?.fontSize ?? "";

  // Save button rendering
  const renderSaveBtn = () => {
    if (!onSave) return null;
    if (saveStatus === "saving") {
      return (
        <button type="button" className="email-save-btn email-save-btn--saving" disabled>
          <Loader2 size={13} className="spin" /> Saving…
        </button>
      );
    }
    if (saveStatus === "saved") {
      return (
        <button type="button" className="email-save-btn email-save-btn--saved" disabled>
          <CheckCircle2 size={13} /> Saved
        </button>
      );
    }
    if (saveStatus === "error") {
      return (
        <button type="button" className="email-save-btn email-save-btn--error" onClick={onSave} disabled={disabled}>
          <XCircle size={13} /> Retry
        </button>
      );
    }
    return (
      <button type="button" className="email-save-btn email-save-btn--idle" onClick={onSave} disabled={disabled}>
        <Save size={13} /> Save
      </button>
    );
  };

  return (
    <div className={`email-editor-wrap${hasError ? " email-editor-wrap--error" : ""}`}>
      {isSourceMode ? (
        <textarea
          className="email-source-textarea"
          value={sourceHtml}
          onChange={(e) => { setSourceHtml(e.target.value); onChange(e.target.value); }}
          disabled={disabled}
          spellCheck={false}
          placeholder="Paste or write your HTML here..."
        />
      ) : (
        <EditorContent editor={editor} />
      )}

      <div className="email-editor-toolbar">
        <div className="email-editor-toolbar-left">

          {/* ── Group 1: Inline formatting ── */}
          <button
            type="button"
            className={`etb${editor?.isActive("bold") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleBold().run()}
            disabled={disabled}
            title="Bold (Ctrl+B)"
          >
            <Bold size={14} />
          </button>
          <button
            type="button"
            className={`etb${editor?.isActive("italic") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            disabled={disabled}
            title="Italic (Ctrl+I)"
          >
            <Italic size={14} />
          </button>
          <button
            type="button"
            className={`etb${editor?.isActive("underline") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            disabled={disabled}
            title="Underline (Ctrl+U)"
          >
            <UnderlineIcon size={14} />
          </button>

          <Divider />

          {/* ── Group 2: Typography ── */}
          <select
            className="etb-select"
            value={currentFontSize}
            onChange={(e) => {
              if (!editor) return;
              if (e.target.value) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor.chain().focus() as any).setFontSize(e.target.value).run();
              } else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor.chain().focus() as any).unsetFontSize().run();
              }
            }}
            disabled={disabled}
            title="Font Size"
          >
            <option value="">Size</option>
            {FONT_SIZES.map((s) => (
              <option key={s} value={s}>
                {s.replace("px", "")}
              </option>
            ))}
          </select>

          {/* Text color */}
          <div className="etb-color-wrap">
            <button
              ref={colorBtnRef}
              type="button"
              className={`etb etb--color${showColorPicker ? " etb--active" : ""}`}
              onClick={() => { setShowColorPicker((v) => !v); setShowHighlightPicker(false); }}
              disabled={disabled}
              title="Text Color"
            >
              <Type size={14} />
              <span
                className="etb-color-bar"
                style={{ background: editor?.getAttributes("textStyle")?.color || "#000" }}
              />
            </button>
            {showColorPicker && (
              <ColorPicker
                colors={TEXT_COLORS}
                onSelect={(color) => {
                  editor?.chain().focus().setColor(color).run();
                }}
                onClose={() => setShowColorPicker(false)}
                customLabel="Custom color"
              />
            )}
          </div>

          {/* Highlight / background color */}
          <div className="etb-color-wrap">
            <button
              ref={highlightBtnRef}
              type="button"
              className={`etb etb--color${showHighlightPicker ? " etb--active" : ""}`}
              onClick={() => { setShowHighlightPicker((v) => !v); setShowColorPicker(false); }}
              disabled={disabled}
              title="Highlight Color"
            >
              <Highlighter size={14} />
              <span
                className="etb-color-bar"
                style={{
                  background:
                    (editor?.isActive("highlight") &&
                      editor.getAttributes("highlight")?.color) ||
                    "#fef08a",
                }}
              />
            </button>
            {showHighlightPicker && (
              <ColorPicker
                colors={HIGHLIGHT_COLORS}
                onSelect={(color) => {
                  if (color === "transparent") {
                    editor?.chain().focus().unsetHighlight().run();
                  } else {
                    editor?.chain().focus().setHighlight({ color }).run();
                  }
                }}
                onClose={() => setShowHighlightPicker(false)}
                customLabel="Custom highlight"
              />
            )}
          </div>

          <Divider />

          {/* ── Group 3: Lists ── */}
          <button
            type="button"
            className={`etb${editor?.isActive("bulletList") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            disabled={disabled}
            title="Bullet List"
          >
            <List size={14} />
          </button>
          <button
            type="button"
            className={`etb${editor?.isActive("orderedList") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            disabled={disabled}
            title="Numbered List"
          >
            <ListOrdered size={14} />
          </button>

          <Divider />

          {/* ── Group 4: Insert ── */}
          <button
            type="button"
            className={`etb${editor?.isActive("link") ? " etb--active" : ""}`}
            onClick={handleLinkToggle}
            disabled={disabled}
            title="Insert Link"
          >
            <Link2 size={14} />
          </button>
          <button
            type="button"
            className="etb"
            onClick={() => setShowImageDialog(true)}
            disabled={disabled}
            title="Insert Image"
          >
            <ImageIcon size={14} />
          </button>

          <Divider />

          {/* ── Group 5: Smart tools ── */}
          <button
            type="button"
            className={`etb${isSourceMode ? " etb--active" : ""}`}
            onClick={handleSourceToggle}
            disabled={disabled}
            title={isSourceMode ? "Switch to Rich Text Editor" : "Edit Raw HTML Source"}
          >
            <Code2 size={14} />
          </button>

          <button
            ref={variablesBtnRef}
            type="button"
            className="etb-text"
            style={isSourceMode ? { opacity: 0.4, pointerEvents: "none" } : undefined}
            onClick={() => {
              setVariablesAnchor(variablesBtnRef.current?.getBoundingClientRect() ?? null);
              setShowVariables(true);
            }}
            disabled={disabled}
            title="Insert Variable"
          >
            <ChevronDown size={12} /> Variables
          </button>
          {hasGoldPlusAccess && (
            <button
              type="button"
              className="etb-text etb-text--ai"
              style={isSourceMode ? { opacity: 0.4, pointerEvents: "none" } : undefined}
              onClick={() => setShowAi(true)}
              disabled={disabled}
              title="AI Writing Assistant"
            >
              <Sparkles size={12} /> AI ✨
            </button>
          )}
        </div>

        {/* ── Save button ── */}
        {renderSaveBtn()}
      </div>

      {/* Modals */}
      {showVariables && (
        <VariablesModal
          editor={editor}
          anchorRect={variablesAnchor}
          onClose={() => { setShowVariables(false); setVariablesAnchor(null); }}
        />
      )}
      {showAi && hasGoldPlusAccess && <AiEmailModal editor={editor} onClose={() => setShowAi(false)} />}
      {showImageDialog && (
        <ImageInsertDialog
          onInsert={handleImageInsert}
          onClose={() => setShowImageDialog(false)}
        />
      )}

      <style>{`
        /* ── Source HTML textarea ── */
        .email-source-textarea {
          display: block;
          width: 100%;
          min-height: 200px;
          padding: 14px 16px;
          font-family: 'Courier New', Courier, monospace;
          font-size: 13px;
          line-height: 1.6;
          color: #1e293b;
          background: #f8fafc;
          border: none;
          outline: none;
          resize: vertical;
          box-sizing: border-box;
        }
        .email-source-textarea:disabled { opacity: 0.5; cursor: not-allowed; }

        /* ── Editor content area ── */
        .email-editor-wrap .ProseMirror {
          min-height: 200px;
          padding: 14px 16px;
          outline: none;
          font-size: 14px;
          line-height: 1.7;
          color: var(--gray-800, #1a202c);
        }
        .email-editor-wrap .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: var(--gray-400, #a0aec0);
          pointer-events: none;
          float: left;
          height: 0;
        }
        .email-editor-wrap {
          border: 1px solid var(--gray-200, #e2e8f0);
          border-radius: 8px;
          overflow: hidden;
          background: #fff;
        }
        .email-editor-wrap--error { border-color: #e53e3e; }
        .email-editor-wrap .ProseMirror ul { padding-left: 22px; list-style-type: disc; }
        .email-editor-wrap .ProseMirror ol { padding-left: 22px; list-style-type: decimal; }
        .email-editor-wrap .ProseMirror a { color: #0284c7; text-decoration: underline; }
        .email-editor-wrap .ProseMirror strong { font-weight: 600; }
        .email-editor-wrap .ProseMirror em { font-style: italic; }
        .email-editor-wrap .ProseMirror u { text-decoration: underline; }
        .email-editor-wrap .ProseMirror img { max-width: 100%; height: auto; display: block; margin: 8px 0; border-radius: 4px; }
        .email-editor-wrap .ProseMirror mark { border-radius: 2px; padding: 0 2px; }

        /* ── Toolbar ── */
        .email-editor-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 10px;
          border-top: 1px solid #f1f5f9;
          background: #f8fafc;
          gap: 2px;
          flex-wrap: wrap;
        }
        .email-editor-toolbar-left {
          display: flex;
          align-items: center;
          gap: 1px;
          flex-wrap: wrap;
        }
        .email-toolbar-divider {
          display: inline-block;
          width: 1px;
          height: 18px;
          background: #e2e8f0;
          margin: 0 4px;
          flex-shrink: 0;
        }

        /* ── Icon buttons ── */
        .etb {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 5px;
          border: none;
          background: transparent;
          cursor: pointer;
          color: #64748b;
          transition: background 0.12s, color 0.12s;
          flex-shrink: 0;
        }
        .etb:hover:not(:disabled) { background: #e2e8f0; color: #1e293b; }
        .etb--active { background: #dbeafe; color: #1d4ed8; }
        .etb--active:hover:not(:disabled) { background: #bfdbfe; }
        .etb:disabled { opacity: 0.4; cursor: not-allowed; }

        /* color button variant */
        .etb--color {
          flex-direction: column;
          gap: 0;
          width: 28px;
          height: 28px;
          padding: 3px 4px 2px;
        }
        .etb-color-bar {
          display: block;
          width: 14px;
          height: 3px;
          border-radius: 2px;
          margin-top: 1px;
          flex-shrink: 0;
        }

        /* ── Font size select ── */
        .etb-select {
          height: 28px;
          padding: 0 4px;
          font-size: 12px;
          border: 1px solid #e2e8f0;
          border-radius: 5px;
          background: #fff;
          color: #374151;
          cursor: pointer;
          outline: none;
          min-width: 54px;
        }
        .etb-select:focus { border-color: #93c5fd; }
        .etb-select:disabled { opacity: 0.4; cursor: not-allowed; }

        /* ── Text buttons ── */
        .etb-text {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 0 8px;
          height: 28px;
          border-radius: 5px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 12px;
          color: #64748b;
          transition: background 0.12s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .etb-text:hover:not(:disabled) { background: #e2e8f0; color: #1e293b; }
        .etb-text:disabled { opacity: 0.4; cursor: not-allowed; }
        .etb-text--ai {
          color: #1e3a5f;
          background: #e0f2fe;
        }
        .etb-text--ai:hover:not(:disabled) { background: #bae6fd; }

        /* ── Color picker popover ── */
        .etb-color-wrap {
          position: relative;
          display: inline-flex;
        }
        .color-picker-popover {
          position: absolute;
          bottom: calc(100% + 6px);
          left: 0;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 10px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          z-index: 1000;
          width: 176px;
        }
        .color-picker-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 5px;
          margin-bottom: 8px;
        }
        .color-swatch {
          width: 32px;
          height: 22px;
          border-radius: 4px;
          cursor: pointer;
          transition: transform 0.1s, box-shadow 0.1s;
          border: 1px solid rgba(0,0,0,0.1);
        }
        .color-swatch:hover { transform: scale(1.12); box-shadow: 0 2px 6px rgba(0,0,0,0.18); }
        .color-picker-custom {
          display: flex;
          align-items: center;
          gap: 6px;
          border-top: 1px solid #f1f5f9;
          padding-top: 8px;
        }
        .color-picker-custom-label {
          font-size: 11px;
          color: #94a3b8;
          flex: 1;
        }
        .color-picker-native {
          opacity: 0;
          position: absolute;
          pointer-events: none;
          width: 0;
          height: 0;
        }
        .color-picker-custom-btn {
          font-size: 11px;
          padding: 3px 8px;
          border-radius: 4px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          cursor: pointer;
          color: #374151;
          white-space: nowrap;
        }
        .color-picker-custom-btn:hover { background: #e2e8f0; }

        /* ── Save button ── */
        .email-save-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 0 14px;
          height: 30px;
          border-radius: 6px;
          border: none;
          font-size: 12.5px;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.15s, color 0.15s, opacity 0.15s;
          flex-shrink: 0;
        }
        .email-save-btn--idle {
          background: #0ea5e9;
          color: #fff;
        }
        .email-save-btn--idle:hover:not(:disabled) { background: #0284c7; }
        .email-save-btn--idle:disabled { opacity: 0.5; cursor: not-allowed; }
        .email-save-btn--saving {
          background: #f1f5f9;
          color: #64748b;
          cursor: not-allowed;
        }
        .email-save-btn--saved {
          background: #dcfce7;
          color: #15803d;
          cursor: default;
        }
        .email-save-btn--error {
          background: #fee2e2;
          color: #b91c1c;
        }
        .email-save-btn--error:hover:not(:disabled) { background: #fecaca; }

        /* spinner animation */
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }

        /* ── Image insert dialog ── */
        .img-dialog-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.35);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .img-dialog {
          background: #fff;
          border-radius: 10px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          width: 400px;
          max-width: 92vw;
          overflow: hidden;
        }
        .img-dialog-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px 12px;
          border-bottom: 1px solid #f1f5f9;
        }
        .img-dialog-title {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
        }
        .img-dialog-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: 5px;
          border: none;
          background: transparent;
          cursor: pointer;
          color: #94a3b8;
        }
        .img-dialog-close:hover { background: #f1f5f9; color: #374151; }
        .img-dialog-tabs {
          display: flex;
          gap: 0;
          padding: 10px 16px 0;
        }
        .img-dialog-tab {
          padding: 6px 16px;
          border-radius: 6px 6px 0 0;
          border: 1px solid transparent;
          border-bottom: none;
          background: transparent;
          font-size: 13px;
          color: #64748b;
          cursor: pointer;
          font-weight: 500;
        }
        .img-dialog-tab.active {
          background: #fff;
          border-color: #e2e8f0;
          color: #0ea5e9;
        }
        .img-dialog-body {
          padding: 14px 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .img-dialog-input {
          width: 100%;
          height: 36px;
          padding: 0 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 13px;
          color: #1e293b;
          outline: none;
          box-sizing: border-box;
        }
        .img-dialog-input:focus { border-color: #93c5fd; box-shadow: 0 0 0 2px #dbeafe; }
        .img-upload-area {
          height: 110px;
          border: 2px dashed #e2e8f0;
          border-radius: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          background: #f8fafc;
          transition: border-color 0.15s, background 0.15s;
          overflow: hidden;
        }
        .img-upload-area:hover { border-color: #93c5fd; background: #f0f9ff; }
        .img-upload-area--has-file { border-style: solid; padding: 4px; }
        .img-upload-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
          color: #94a3b8;
          font-size: 13px;
        }
        .img-upload-hint { font-size: 11px; color: #cbd5e1; }
        .img-upload-preview {
          max-height: 100%;
          max-width: 100%;
          object-fit: contain;
          border-radius: 4px;
        }
        .img-upload-spinner { animation: spin 0.8s linear infinite; color: #94a3b8; }
        .img-dialog-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 12px 16px 14px;
          border-top: 1px solid #f1f5f9;
        }
        .img-dialog-cancel {
          padding: 7px 16px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          background: #fff;
          font-size: 13px;
          color: #374151;
          cursor: pointer;
          font-weight: 500;
        }
        .img-dialog-cancel:hover { background: #f8fafc; }
        .img-dialog-insert {
          padding: 7px 16px;
          border-radius: 6px;
          border: none;
          background: #0ea5e9;
          font-size: 13px;
          color: #fff;
          cursor: pointer;
          font-weight: 500;
        }
        .img-dialog-insert:hover:not(:disabled) { background: #0284c7; }
        .img-dialog-insert:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
